import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {subMenuManagement} from "../../../../utils/Constans";
import './side-nav.css';

import {getMemberRole} from "../../../../utils/General";
import config from "../../../../config";
import MenuItem from "./MenuItem";
import {connect} from "react-redux";
import HomeIcon from "../../../../assets/icons/nav/HomeIcon";
import InternalChatsIcon from "../../../../assets/icons/nav/InternalChatsIcon";
import MySpaceIcon from "../../../../assets/icons/nav/MySpaceIcon";
import InternalGroupsIcon from "../../../../assets/icons/nav/InternalGroupsIcon";
import BillingIcon from "../../../../assets/icons/nav/BillingIcon";
import ForumIcon from "../../../../assets/icons/ForumIcon";

const SideNav = ({
                     permissions,
                     clients,
                     user,
                     company,
                     conversationsList,
                     collapsed,
                     mobileOpen,
                     onCloseMobile,
                 }) => {
    const role = getMemberRole(user).id;
    const {tariff} = company;
    const {features} = tariff;

    const isActiveTariff = [config.tariffStatusMap.active, config.tariffStatusMap.trial].includes(tariff.status);
    const isManager = permissions.includes(config.permissions.management);

    const [internalUnread, setInternalUnread] = useState(0);
    const [allChatsUnread, setAllChatsUnread] = useState(0);

    const [animate, setAnimate] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setAnimate(true);
        }, 50); // small delay to ensure CSS sees the change

        return () => clearTimeout(timer);
    }, []);
    useEffect(() => {
        let internal = 0;
        let allChats = 0;

        conversationsList.forEach(conv => {
            if (conv.unread > 0) {
                if (conv.platform === config.platformsMap.company) {
                    internal += conv.unread;
                } else {
                    allChats += conv.unread;
                }
            }
        });

        setInternalUnread(internal);
        setAllChatsUnread(allChats);
    }, [conversationsList]);

    const navMenuData = [];

    // Home - the landing, and the only entry that every role shares
    if (isActiveTariff && features.includes(config.features.STATISTICS)) {
        navMenuData.push({
            index: -1,
            id: 'sidebar-link-home',
            path: '/dashboard/home',
            alt: 'home',
            name: 'dashboardTitle',
            icon: HomeIcon,
        });
    }

    // Internal Chats
    if (isActiveTariff && features.includes(config.features.INTERNAL_CHATS)) {
        navMenuData.push({
            index: -1,
            id: 'sidebar-link-member-chat',
            path: `/dashboard/chat/${config.platformsMap.company}`,
            alt: 'members-chat',
            name: 'MembersChat',
            icon: InternalChatsIcon,
            unread: internalUnread,
        });
    }

    // All Chats
    if (isActiveTariff && (clients.length || isManager)) {
        navMenuData.push({
            index: -1,
            id: 'sidebar-link-chat',
            path: '/dashboard/chat/all',
            name: 'all-chats',
            alt: 'all-chats',
            icon: ForumIcon,
            unread: allChatsUnread,
        });
    }

    // My Space
    if (isActiveTariff && (features.includes(config.features.MY_HISTORY) || features.includes(config.features.TICKETS))) {
        navMenuData.push({
            index: -1,
            id: 'sidebar-link-my-history',
            path: '/dashboard/my/space',
            alt: 'my-space',
            name: 'My Space',
            icon: MySpaceIcon,
        });
    }

    if(isActiveTariff && (features.includes(config.features.INTERNAL_GROUPING)) && permissions.includes(config.permissions.management)) {
        navMenuData.push({
            index: -1,
            id: 'sidebar-link-internal-groups',
            path: '/dashboard/internal-groups',
            alt: 'internal-groups',
            name: 'Internal Groups',
            icon: InternalGroupsIcon,
        });
    }

    // Client-Specific Chats
    if (isActiveTariff && user.authorities.includes(config.rolesMap.agent)) {
        clients.forEach((client, index) => {
            navMenuData.push({
                index,
                id: client.id,
                imgSrc: client.image,
                name: client.name,
                alt: 'client-chat',
                path: `/dashboard/chat/${client.id}`,
            });
        });
    }

    // Management
    const filteredSubmenus = subMenuManagement
        .filter((item) => item.permissions.includes(role))
        .filter((item) => !item.features || features.includes(item.features[0]));

    if (isManager && isActiveTariff) {
        filteredSubmenus.forEach((sm) => {
            navMenuData.push({
                index: sm.index,
                id: sm.id,
                path: sm.path,
                name: sm.name,
                alt: sm.alt,
                icon: sm.icon,
            });
        });
    } else if (tariff.status === config.tariffStatusMap.paymentRequired && isManager) {
        filteredSubmenus
            .filter((item) => item.name === 'Settings')
            .forEach((sm) => {
                navMenuData.push({
                    index: sm.index,
                    id: sm.id,
                    path: sm.path,
                    name: sm.name,
                    alt: sm.alt,
                    icon: sm.icon,
                });
            });
    }

    // Billing
    if (permissions.includes(config.permissions.billing) && features.includes(config.features.BILLING)) {
        navMenuData.push({
            index: 2000,
            id: 'sidebar-link-billing',
            path: '/dashboard/billing',
            name: 'Billing',
            alt: 'billing',
            icon: BillingIcon,
        });
    }

    navMenuData.sort((a, b) => a.index - b.index);
    const showItemTooltips = collapsed && !mobileOpen;

    return (
        <div
            id="dashboard-navigation"
            className={`parent-sidebar-div ${animate ? 'animate' : ''} ${collapsed ? 'collapsed' : 'expanded'} ${mobileOpen ? 'mobile-open' : ''}`}
        >
            {/* The unread mark and the submenu list used to be passed in here as
                children. MenuItem rendered children as `children && toggler &&
                children`, and setToggler(true) is called nowhere - so neither had
                ever appeared on screen. nav.subMenu was never set either: the
                management entries are pushed into this list flat. The count now
                goes through as a prop and the submenu machinery is gone. */}
            <ul className="no-style-list sidebar-menu-list">
                {navMenuData.map((nav) => (
                    <MenuItem
                        key={nav.id}
                        {...nav}
                        user={user}
                        onNavigate={onCloseMobile}
                        showTooltip={showItemTooltips}
                    />
                ))}
            </ul>
        </div>
    );
};

SideNav.propTypes = {
    clients: PropTypes.array.isRequired,
    permissions: PropTypes.array.isRequired,
    user: PropTypes.object.isRequired,
    company: PropTypes.object.isRequired,
    conversationsList: PropTypes.array,
    collapsed: PropTypes.bool,
    mobileOpen: PropTypes.bool,
    onCloseMobile: PropTypes.func,
};

SideNav.defaultProps = {
    conversationsList: [],
    collapsed: false,
    mobileOpen: false,
    onCloseMobile: () => {},
};

const mapStateToProps = state => ({
    conversationsList: state.chat.conversationsList,
});


export default connect(mapStateToProps, null)(SideNav);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/side-bar/SideNav.jsx